<script setup lang="ts">
import { ref, onBeforeMount, onUpdated } from "vue";

import Icon from "@/Components/Extend/TcIcon.vue";

export interface TcCheckboxProps {
  label?: string;
  disabled?: boolean;
  position?: "left" | "right";
  direction?: "row" | "column";
  modelValue?: boolean;
  value?: boolean;
}

const props = withDefaults(defineProps<TcCheckboxProps>(), {
  position: "left",
  disabled: false,
  direction: "column",
});

const value = ref<boolean>();

const emit = defineEmits(["update:modelValue", "change"]);

const updateValue = () => {
  if (!props.disabled) {
    value.value = !value.value;
  }

  emit("update:modelValue", value.value);
  emit("change", value.value);
};

const syncValue = () => {
  value.value = (props.modelValue || props.value) === true;
};

onBeforeMount(syncValue);
onUpdated(syncValue);
</script>

<template>
  <div @click="updateValue" class="checkbox" :class="{ disabled: props.disabled }">
    <div
      :class="{
        'ml-2': position === 'right',
        'mr-2': position === 'left',
        [position]: true,
      }"
      class="checkbox__icon"
    >
      <Icon :name="value === false ? 'CheckboxEmpty' : 'CheckboxChecked'" color="content-high" />
    </div>
    <div class="checkbox__label" :class="`checkbox__label--${direction}`">
      <slot name="label">
        {{ label }}
      </slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.checkbox {
  cursor: pointer;
  display: flex;
  align-items: center;

  &.disabled {
    cursor: not-allowed;
    .checkbox__icon {
      opacity: 30%;
    }
  }

  &__icon {
    display: flex;
    &.right {
      order: 2;
    }
    &.left {
      order: 0;
    }
  }

  &__label {
    @include text.general(low, l);
    @include colors.color(content, high);

    display: flex;
    order: 1;

    &--column {
      flex-direction: column;
    }

    &--row {
      flex-direction: row;
    }
  }
}
</style>
